import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { WrenchIcon, CoffeeIcon, CheckIcon } from '../components/Icons';
import './HowItWorks.css';

const commercialPlantSlides = [
  { src: '/images/CCPshopify1.webp', alt: 'Commercial RO skid render', link: 'https://watershopssa.co.za/products/1000-gpd-ro-system' },
  { src: '/images/CCPshopify2.webp', alt: 'PLC automation station', link: 'https://watershopssa.co.za/products/advanced-compact-eco-2000gpd-ro-system' },
  { src: '/images/CCPshopify3.webp', alt: 'High capacity membrane rack', link: 'https://watershopssa.co.za/products/2000gpd-ro-system' },
  { src: '/images/CCPshopify4.webp', alt: 'Bulk storage and blending tanks', link: 'https://watershopssa.co.za/products/compact-4000gpd-ro' },
  { src: '/images/CCPshopify5.webp', alt: 'Containerized layout concept', link: 'https://watershopssa.co.za/products/ksro-series-reverse-osmosis-system' },
  { src: '/images/CCPshopify6.webp', alt: 'SCADA visualization', link: 'https://watershopssa.co.za/products/compact-4000gpd-ro-with-permeate-flush' }
];

const waterShopSlides = [
  { src: '/images/1000-Litres-Per-Hour-Water-Purification-System-On-Brackish-Water-scaled.webp', alt: 'Premium water shop counter layout', link: 'https://watershopssa.co.za/products/1000-litres-per-hour-water-purification-system-on-brackish-water' },
  { src: '/images/1000-Litres-Per-Hour-Standard-Water-Purification-System-scaled.webp', alt: 'Bottling bay with RO tanks', link: 'https://watershopssa.co.za/products/1000-litres-per-hour-standard-water-purification-system' },
  { src: '/images/500-Litres-Per-Hour-Standard-Water-Purification-Plant-scaled.webp', alt: 'Pure Drop store equipment bay', link: 'https://watershopssa.co.za/products/500-litres-per-hour-standard-water-purification-plant' },
  { src: '/images/500-Litres-Per-Hour-Water-Purification-Plant-On-Brackish-Water-scaled.webp', alt: 'Point-of-sale and refill area', link: 'https://watershopssa.co.za/products/500-litres-per-hour-water-purification-plant-on-brackish-water' },
  { src: '/images/250-Litres-Per-Hour-Water-Purification-Plant-On-Brackish-Water-scaled.webp', alt: 'Exterior signage concept', link: 'https://watershopssa.co.za/products/250-litres-per-hour-water-purification-plant-on-brackish-water' },
  { src: 'images/250-Litres-Per-Hour-Standard-Water-Purification-System-scaled.webp', alt: 'Technicians staging installations', link: 'https://watershopssa.co.za/products/250-litres-per-hour-standard-water-purification-system' }
];

const ShopCarousel = ({ slides = [], shopLink }) => {
  const [index, setIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < 1024);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const prev = () => setIndex((i) => (i - 1 + slides.length) % slides.length);
  const next = () => setIndex((i) => (i + 1) % slides.length);

  useEffect(() => {
    if (isMobile) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 3500);
    return () => clearInterval(id);
  }, [isMobile, slides.length]);

  if (!slides.length) return null;

  if (isMobile) {
    return (
      <div className="shop-carousel">
        <div className="product-grid">
          {slides.map((item, idx) => (
            <a
              className="carousel-card"
              key={idx}
              href={item.link || shopLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="carousel-img-wrap">
                <img src={item.src} alt={item.alt} loading="lazy" />
              </div>
              <div className="carousel-info">
                <div className="carousel-title">{item.alt}</div>
                <span className="btn btn-primary carousel-shop-btn">Shop Now</span>
              </div>
            </a>
          ))}
        </div>
        {shopLink && (
          <a
            href={shopLink}
            className="btn btn-outline carousel-collection-btn"
            target="_blank"
            rel="noopener noreferrer"
          >
            Shop Collection
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="shop-carousel">
      <div className="carousel-track" style={{ transform: `translateX(-${index * 100}%)` }}>
        {slides.map((item, idx) => (
          <a
            className="carousel-card"
            key={idx}
            href={item.link || shopLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="carousel-img-wrap">
              <img src={item.src} alt={item.alt} loading="lazy" />
            </div>
            <div className="carousel-info">
              <div className="carousel-title">{item.alt}</div>
              <span className="btn btn-primary carousel-shop-btn">Shop Now</span>
            </div>
          </a>
        ))}
      </div>
      <div className="carousel-nav">
        <button onClick={prev} aria-label="Previous product">‹</button>
        <button onClick={next} aria-label="Next product">›</button>
      </div>
      <div className="carousel-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`dot ${i === index ? 'active' : ''}`}
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => setIndex(i)}
          />
        ))}
      </div>
      {shopLink && (
        <a
          href={shopLink}
          className="btn btn-outline carousel-collection-btn"
          target="_blank"
          rel="noopener noreferrer"
        >
          Shop Collection
        </a>
      )}
    </div>
  );
};

const HowItWorks = () => {
  return (
    <div className="how-it-works-page">
      <Helmet>
        <title>How It Works - Water Business Setup Process</title>
        <meta
          name="description"
          content="Our streamlined 3-step process for starting your water business: Plant Setup, Packaging Solutions, and Launch & Sell."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="hiw-hero section small-hero">
        <div className="container">
          <div className="hiw-hero-content">
            <div className="hiw-text">
              <h1>How It Works</h1>
              <p className="lead">Design, install, and open your water shop in three fast stages.</p>
              <div className="hero-cta">
                <Link to="/request" className="btn btn-primary">Start a Project</Link>
                <Link to="#process" className="btn btn-outline">View Steps</Link>
              </div>
            </div>
            <div className="hiw-visual" aria-hidden="true">
              <div className="hiw-icon">
                <WrenchIcon size={56} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section id="process" className="process-steps section">
        <div className="container">
          <div className="steps-container">

            {/* Step 1 — Plant Setup */}
            <div className="step-item">
              <div className="step-visual">
                <div className="step-number">1</div>
                <div className="step-icon">
                  <WrenchIcon size={64} className="step-icon-svg" />
                </div>
              </div>
              <div className="step-content">
                <h2>Plant Setup</h2>
                <p className="step-description">
                  Professional installation of your water purification system with full testing and quality assurance.
                </p>

                <div className="step-details">
                  <h4>Installation Process:</h4>
                  <ul>
                    <li>Site assessment and preparation</li>
                    <li>RO system and equipment installation</li>
                    <li>Plumbing and electrical connections</li>
                    <li>Water quality testing and calibration</li>
                    <li>Safety systems and monitoring setup</li>
                    <li>Backup power and redundancy systems</li>
                    <li>Final inspection and certification</li>
                  </ul>
                </div>

                <div className="step-timeline">
                  <span className="timeline-duration">Duration: 3–5 business days</span>
                </div>

                {/* Media placeholders for this block */}
                <div className="step-media">
                  <div className="visual-box" style={{ marginTop: 20 }}>
                    <h3 style={{ marginBottom: 16, textAlign: 'center' }}>Gallery</h3>
                    <div className="shop-layout">
                      <div className="layout-item media-placeholder">
                        <img src="/images/CPPlant1.png" alt="Commercial RO skid render" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/CClant2.png" alt="PLC automation station" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/CCPlant3.png" alt="High capacity membrane rack" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/CPPlant4.png" alt="Bulk storage and blending tanks" loading="lazy" />
                      </div>
                    </div>
                  </div>

                  <div className="visual-box" style={{ marginTop: 20 }}>
                    <h3 style={{ marginBottom: 16, textAlign: 'center' }}>Products</h3>
                    <ShopCarousel
                      slides={commercialPlantSlides}
                      shopLink="https://watershopssa.co.za/pages/reverse-osmosis-systems"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 — Packaging Solutions */}
            <div className="step-item reverse">
              <div className="step-content">
                <h2>Packaging Solutions</h2>
                <p className="step-description">
                  Complete packaging setup including bottle filling equipment, labeling, and branding materials.
                </p>

                <div className="step-details">
                  <h4>Packaging Setup:</h4>
                  <ul>
                    <li>Bottle filling and sealing machines</li>
                    <li>Custom label design and printing</li>
                    <li>Bottle inventory and storage systems</li>
                    <li>Quality control and batch tracking</li>
                    <li>Packaging material suppliers setup</li>
                    <li>Branding and marketing materials</li>
                    <li>Point-of-sale systems installation</li>
                  </ul>
                </div>

                <div className="step-timeline">
                  <span className="timeline-duration">Duration: 2–3 business days</span>
                </div>

                {/* Media placeholders removed – this step focuses on the process overview */}
              </div>
              <div className="step-visual">
                <div className="step-number">2</div>
                <div className="step-icon">
                  <CoffeeIcon size={64} className="step-icon-svg" />
                </div>
              </div>
            </div>

            {/* Step 3 — Launch & Sell */}
            <div className="step-item">
              <div className="step-visual">
                <div className="step-number">3</div>
                <div className="step-icon">
                  <CheckIcon size={64} className="step-icon-svg" />
                </div>
              </div>
              <div className="step-content">
                <h2>Launch & Sell Water</h2>
                <p className="step-description">
                  Training, marketing support, and ongoing assistance to ensure your successful business launch.
                </p>

                <div className="step-details">
                  <h4>Launch Support:</h4>
                  <ul>
                    <li>Staff training on operations and safety</li>
                    <li>Marketing strategy and materials</li>
                    <li>Customer acquisition assistance</li>
                    <li>Pricing strategy optimization</li>
                    <li>Distribution network setup</li>
                    <li>Financial management training</li>
                    <li>Ongoing technical support</li>
                  </ul>
                </div>

                <div className="step-timeline">
                  <span className="timeline-duration">Start earning immediately!</span>
                </div>

                {/* Media placeholders for this block */}
                <div className="step-media">
                  <div className="visual-box" style={{ marginTop: 20 }}>
                    <h3 style={{ marginBottom: 16, textAlign: 'center' }}>Gallery</h3>
                    <div className="shop-layout">
                      <div className="layout-item media-placeholder">
                        <img src="/images/watershopS1.jpeg" alt="Retail water shop counter concept" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/watershopS2.jpeg" alt="Bottling racks and filling points illustration" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/watershopS3.jpeg" alt="Interior layout for a Pure Drop store" loading="lazy" />
                      </div>
                      <div className="layout-item media-placeholder">
                        <img src="/images/watershopS4.jpeg" alt="Equipment bay with RO skids preview" loading="lazy" />
                      </div>
                    </div>
                  </div>

                  <div className="visual-box" style={{ marginTop: 20 }}>
                    <h3 style={{ marginBottom: 16, textAlign: 'center' }}>Products</h3>
                    <ShopCarousel
                      slides={waterShopSlides}
                      shopLink="https://watershopssa.co.za/collections/watershops"
                    />
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Timeline Overview (Legal removed) */}
      <section className="timeline-overview section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Complete Process Timeline</h2>
            <p>From consultation to cash flow in just 2–3 weeks</p>
          </div>

          <div className="timeline-visual">
            <div className="timeline-track">
              <div className="timeline-point">
                <div className="point-marker">Day 1</div>
                <div className="point-label">Initial Consultation</div>
              </div>
              <div className="timeline-point">
                <div className="point-marker">Week 1</div>
                <div className="point-label">Installation</div>
              </div>
              <div className="timeline-point">
                <div className="point-marker">Week 2</div>
                <div className="point-label">Packaging & Branding</div>
              </div>
              <div className="timeline-point final">
                <div className="point-marker">Week 3</div>
                <div className="point-label">Launch & Training</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investment & ROI (updated small water shop range) */}
      <section className="investment-roi section">
        <div className="container">
          <div className="investment-content">
            <div className="investment-text">
              <h2>Investment & Return Analysis</h2>
              <p>Understanding the financial aspects of your water business investment</p>

              <div className="investment-breakdown">
                <h4>Typical Investment Ranges:</h4>
                <div className="investment-types">
                  <div className="investment-type">
                    <h5>Small Water Shop</h5>
                    <div className="price-range">R5,000 - R300,000</div>
                    <ul>
                      <li>Basic RO system (up to ±1,000 L/day)</li>
                      <li>Essential equipment</li>
                      <li>Standard setup</li>
                    </ul>
                  </div>

                  <div className="investment-type">
                    <h5>Medium Commercial Plant</h5>
                    <div className="price-range">R500,000 - R1,200,000</div>
                    <ul>
                      <li>Commercial RO system (±5,000 L/day)</li>
                      <li>Advanced equipment</li>
                      <li>Full automation</li>
                    </ul>
                  </div>

                  <div className="investment-type">
                    <h5>Large Industrial Plant</h5>
                    <div className="price-range">R1,500,000+</div>
                    <ul>
                      <li>Industrial systems (20,000 L+/day)</li>
                      <li>Premium equipment</li>
                      <li>Complete automation</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Static example ROI box */}
            <div className="roi-calculator">
              <div className="calculator-box">
                <h3>ROI Calculator Example</h3>
                <div className="calc-scenario">
                  <h4>Small Water Shop Scenario:</h4>
                  <div className="calc-row"><span>Initial Investment:</span><span>R250,000</span></div>
                  <div className="calc-row"><span>Daily Production:</span><span>1,000 litres</span></div>
                  <div className="calc-row"><span>Selling Price:</span><span>R2.50/litre</span></div>
                  <div className="calc-row"><span>Daily Revenue:</span><span>R2,500</span></div>
                  <div className="calc-row"><span>Monthly Revenue:</span><span>R75,000</span></div>
                  <div className="calc-row total"><span>Break-even Period:</span><span>6–8 months</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="success-stories section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Success Stories</h2>
            <p>Real businesses, real results with Pure Drop Technicals</p>
          </div>

          <div className="stories-grid">
            <div className="story-card">
              <div className="story-header">
                <h4>Thabo's Water Shop - Soweto</h4>
                <div className="story-type">Small Water Shop</div>
              </div>
              <div className="story-content">
                <p>
                  "Started with R200,000 investment. Now serving 500+ customers daily and generating
                  R60,000+ monthly revenue. The support from Pure Drop made all the difference."
                </p>
                <div className="story-stats">
                  <span>Monthly Revenue: R60,000+</span>
                  <span>Daily Customers: 500+</span>
                  <span>Payback: 7 months</span>
                </div>
              </div>
            </div>

            <div className="story-card">
              <div className="story-header">
                <h4>Nomsa's Commercial Plant - Durban</h4>
                <div className="story-type">Commercial Plant</div>
              </div>
              <div className="story-content">
                <p>
                  "Expanded from water shop to commercial plant. Now supplying 3 schools and 2 businesses.
                  Investment doubled in 18 months with Pure Drop's guidance."
                </p>
                <div className="story-stats">
                  <span>Monthly Revenue: R180,000+</span>
                  <span>B2B Contracts: 5</span>
                  <span>ROI: 200% in 18 months</span>
                </div>
              </div>
            </div>

            <div className="story-card">
              <div className="story-header">
                <h4>Ahmed's Alkaline Water - Cape Town</h4>
                <div className="story-type">Alkaline System</div>
              </div>
              <div className="story-content">
                <p>
                  "Premium alkaline water business targeting health-conscious customers.
                  Higher margins and loyal customer base. Pure Drop's training was exceptional."
                </p>
                <div className="story-stats">
                  <span>Monthly Revenue: R95,000+</span>
                  <span>Premium Product: 40% higher margins</span>
                  <span>Loyal Customers: 300+</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="hiw-cta section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Start Your Journey?</h2>
            <p>Join hundreds of successful water entrepreneurs across South Africa</p>
            <div className="cta-buttons">
              <Link to="/request" className="btn btn-primary">Start Your Business</Link>
              <Link to="/contact" className="btn btn-outline">Ask Questions</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
